import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingUp,
  Users,
  IndianRupee,
  Target,
  Trophy,
  Save,
  Flame,
  ClipboardList,
  HelpCircle,
} from "lucide-react";
import { SOURCE_COLORS, STATUS_COLORS, formatINR, LEAD_SOURCES } from "@/lib/constants";
import NotificationBell from "@/components/NotificationBell";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ConversionRing = ({ pct }) => {
  const R = 56;
  const C = 2 * Math.PI * R;
  const clamped = Math.min(100, Math.max(0, pct || 0));
  const offset = C * (1 - clamped / 100);
  return (
    <div className="relative w-[160px] h-[160px]" data-testid="conversion-ring">
      <svg width="160" height="160" className="-rotate-90">
        <circle cx="80" cy="80" r={R} fill="none" stroke="#eef2f7" strokeWidth="13" />
        <circle
          cx="80"
          cy="80"
          r={R}
          fill="none"
          stroke="#2563eb"
          strokeWidth="13"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 700ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-4xl font-bold tracking-tighter text-slate-900">
          {clamped}%
        </span>
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
          last 30 days
        </span>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, sub, icon: Icon, accent, testid, onClick }) => (
  <div
    data-testid={testid}
    onClick={onClick}
    className={`card-hover bg-white rounded-xl border border-slate-200 p-5 shadow-sm ${
      onClick ? "cursor-pointer hover:border-blue-300" : ""
    }`}
  >
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}
      >
        <Icon size={18} />
      </div>
    </div>
    <div className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
      {value}
    </div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function Dashboard({ stats, team, refetchAll, setView, onRevenueMonthClick }) {
  const [target, setTarget] = useState(0);
  const [savingTarget, setSavingTarget] = useState(false);
  const [selMonth, setSelMonth] = useState(null);
  const [revMonth, setRevMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => {
    if (stats) setTarget(stats.monthly_target || 0);
  }, [stats]);

  if (!stats) return null;
  const sources = LEAD_SOURCES;

  const topRep = stats.team_performance?.[0];

  const monthRevenue = stats.month_revenue || 0;
  const revStats =
    revMonth === "all"
      ? { value: stats.total_revenue || 0, sub: "Sum of all closed deals" }
      : (() => {
          const m = (stats.monthly_leads || []).find((x) => x.key === revMonth);
          return { value: m ? m.revenue || 0 : 0, sub: `Closed in ${m ? m.month : "this month"}` };
        })();
  const tgt = Number(target) || 0;
  const remaining = Math.max(0, tgt - monthRevenue);
  const progressPct = tgt > 0 ? Math.min(100, (monthRevenue / tgt) * 100) : 0;
  const convFrac = (stats.conversion_rate || 0) / 100;
  const avgDeal = stats.avg_deal_value || 0;
  const leadsNeeded =
    remaining <= 0 && tgt > 0
      ? 0
      : avgDeal > 0 && convFrac > 0
      ? Math.ceil(remaining / (avgDeal * convFrac))
      : null;

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      await axios.put(`${API}/settings/target`, { monthly_target: Number(target) || 0 });
      toast.success("Monthly target updated");
      await refetchAll();
    } catch {
      toast.error("Failed to update target");
    } finally {
      setSavingTarget(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1500px] w-full mx-auto">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Sales command center
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Real-time pipeline health for Techborn Ventures · updated live
          </p>
        </div>
        <NotificationBell />
      </header>

      {stats.hot_pending && stats.hot_pending.length > 0 && (
        <button
          data-testid="dashboard-hot-alert"
          onClick={() => setView && setView("library")}
          className="w-full mb-6 flex items-center gap-3 px-4 py-3 rounded-xl border bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 text-left"
          style={{ transition: "background-color 160ms ease" }}
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-rose-100">
            <Flame size={18} className="flicker" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-bold text-sm">
              {stats.hot_pending.length} unreviewed hot lead{stats.hot_pending.length !== 1 ? "s" : ""} need attention
            </span>
            <span className="block text-xs opacity-90 truncate">
              {stats.hot_pending.slice(0, 3).map((h) => h.company_name).join(" · ")}
              {stats.hot_pending.length > 3 ? " …" : ""}
            </span>
          </span>
          <span className="text-xs font-bold uppercase tracking-wide">Open library</span>
        </button>
      )}

      {stats.open_questions && stats.open_questions.length > 0 && (
        <button
          data-testid="dashboard-open-questions"
          onClick={() => setView && setView("library")}
          className="w-full mb-6 flex items-center gap-3 px-4 py-3 rounded-xl border bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 text-left"
          style={{ transition: "background-color 160ms ease" }}
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-rose-100">
            <HelpCircle size={18} className="flicker" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-bold text-sm">
              {stats.open_questions.length} lead{stats.open_questions.length !== 1 ? "s" : ""} need{stats.open_questions.length === 1 ? "s" : ""} an answer
            </span>
            <span className="block text-xs opacity-90 truncate">
              {stats.open_questions.slice(0, 3).map((h) => h.company_name).join(" · ")}
              {stats.open_questions.length > 3 ? " …" : ""}
            </span>
          </span>
          <span className="text-xs font-bold uppercase tracking-wide">Open library</span>
        </button>
      )}

      {stats.orders_summary && stats.orders_summary.due_soon > 0 && (
        <button
          data-testid="dashboard-duesoon"
          onClick={() => setView && setView("orders")}
          className="w-full mb-6 flex items-center gap-3 px-4 py-3 rounded-xl border bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100 text-left"
          style={{ transition: "background-color 160ms ease" }}
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100">
            <ClipboardList size={18} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-bold text-sm">
              {stats.orders_summary.due_soon} order{stats.orders_summary.due_soon !== 1 ? "s" : ""} due within 3 days
            </span>
            <span className="block text-xs opacity-90">Follow up before delivery dates slip</span>
          </span>
          <span className="text-xs font-bold uppercase tracking-wide">Open orders</span>
        </button>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-6">
        <KpiCard
          testid="kpi-total-leads"
          label="Total leads · 30d"
          value={stats.total_leads_30d}
          sub={`${stats.total_leads} lifetime leads`}
          icon={Users}
          accent="bg-blue-50 text-blue-700"
        />
        <KpiCard
          testid="kpi-conversion"
          label="Global conversion"
          value={`${stats.conversion_rate}%`}
          sub={`${stats.total_won} deals won`}
          icon={Target}
          accent="bg-violet-50 text-violet-700"
        />
        <div data-testid="kpi-revenue" className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Sales revenue</div>
              <button
                type="button"
                data-testid="revenue-value"
                onClick={() => onRevenueMonthClick && onRevenueMonthClick(revMonth)}
                title="View these closed deals in the Lead Library"
                className="font-heading text-2xl font-bold text-slate-900 mt-1 text-left hover:text-emerald-700 hover:underline"
                style={{ transition: "color 150ms ease" }}
              >
                {formatINR(revStats.value)}
              </button>
              <div className="text-xs text-slate-400 mt-1 truncate">{revStats.sub}</div>
            </div>
            <span className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
              <IndianRupee size={18} />
            </span>
          </div>
          <Select value={revMonth} onValueChange={setRevMonth}>
            <SelectTrigger data-testid="revenue-month-select" className="h-8 mt-3 text-xs rounded-md border-slate-200 shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg">
              <SelectItem value="all" className="rounded-md text-xs">All time (total)</SelectItem>
              {(stats.monthly_leads || []).slice().reverse().map((m) => (
                <SelectItem key={m.key} value={m.key} className="rounded-md text-xs">{m.month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <KpiCard
          testid="kpi-top-rep"
          label="Top performer"
          value={topRep ? topRep.name : "—"}
          sub={topRep ? formatINR(topRep.value) + " generated" : "No deals won yet"}
          icon={Trophy}
          accent="bg-amber-50 text-amber-700"
        />
        <KpiCard
          testid="kpi-orders"
          label="Current orders"
          value={stats.orders_summary ? stats.orders_summary.active : 0}
          sub={
            stats.orders_summary
              ? `${stats.orders_summary.total} total · ${stats.orders_summary.delayed} delayed`
              : "Last 30 days"
          }
          icon={ClipboardList}
          accent="bg-indigo-50 text-indigo-700"
          onClick={() => setView && setView("orders")}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div
          data-testid="conversion-ratio-card"
          className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col items-center"
        >
          <div className="w-full flex items-center justify-between mb-2">
            <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
              Conversion ratio
            </h2>
            <Target size={18} className="text-slate-400" />
          </div>
          <ConversionRing pct={stats.conversion_30d} />
          <div className="text-xs text-slate-500 mt-3 text-center">
            <span className="font-semibold text-slate-900">{stats.won_30d}</span> of{" "}
            <span className="font-semibold text-slate-900">{stats.total_leads_30d}</span> leads
            closed in the last 30 days
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 w-full text-center">
            <div className="text-[11px] uppercase tracking-[0.12em] text-slate-400 font-semibold">
              Lifetime conversion
            </div>
            <div
              data-testid="lifetime-conversion"
              className="font-heading text-2xl font-bold text-slate-900 mt-0.5"
            >
              {stats.conversion_rate}%
            </div>
            <div className="text-xs text-slate-500">
              {stats.total_won} won of {stats.total_leads} total leads
            </div>
          </div>
        </div>

        <div
          data-testid="sales-target-card"
          className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
                Monthly sales target
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Track this month's revenue against your goal
              </p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1 block">
                  Target (₹)
                </label>
                <input
                  data-testid="target-input"
                  type="number"
                  min="0"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  className="w-40 h-9 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
              <button
                data-testid="save-target-btn"
                onClick={saveTarget}
                disabled={savingTarget}
                className="inline-flex items-center gap-1.5 h-9 px-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-md"
                style={{ transition: "background-color 160ms ease" }}
              >
                <Save size={15} />
                Save
              </button>
            </div>
          </div>

          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-sm font-semibold text-slate-900" data-testid="month-revenue">
              {formatINR(monthRevenue)}
            </span>
            <span className="text-xs text-slate-500">of {formatINR(tgt)}</span>
          </div>
          <div className="h-3.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              data-testid="target-progress"
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
              style={{ width: `${progressPct}%`, transition: "width 500ms ease" }}
            />
          </div>
          <div className="text-xs text-slate-500 mt-1.5">
            {tgt > 0
              ? `${Math.round(progressPct)}% achieved · ${formatINR(remaining)} to go`
              : "Set a target to start tracking progress"}
          </div>

          <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-slate-100">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-400 font-semibold">
                Leads needed
              </div>
              <div
                data-testid="leads-needed"
                className="font-heading text-2xl font-bold text-slate-900 mt-0.5"
              >
                {leadsNeeded === null ? "—" : leadsNeeded}
              </div>
              <div className="text-[11px] text-slate-500">to hit target</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-400 font-semibold">
                Conversion rate
              </div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">
                {stats.conversion_rate}%
              </div>
              <div className="text-[11px] text-slate-500">lifetime close rate</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-400 font-semibold">
                Avg deal size
              </div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">
                {formatINR(avgDeal)}
              </div>
              <div className="text-[11px] text-slate-500">per won deal</div>
            </div>
          </div>
        </div>
      </div>

      <div
        data-testid="chart-revenue-monthly"
        className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
              Won revenue · last 12 months
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Revenue recognised in the month each deal was won · click a bar to view those deals
            </p>
          </div>
          <IndianRupee size={18} className="text-slate-400" />
        </div>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stats.monthly_leads || []}
              onClick={(e) => {
                if (e && e.activeLabel && onRevenueMonthClick) {
                  const m = (stats.monthly_leads || []).find((x) => x.month === e.activeLabel);
                  if (m && (m.revenue || 0) > 0) onRevenueMonthClick(m.key);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                width={70}
                tickFormatter={(v) => formatINR(v)}
              />
              <Tooltip
                cursor={{ fill: "#f1f5f9" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                formatter={(v) => [formatINR(v), "Won revenue"]}
              />
              <Bar dataKey="revenue" fill="#16a34a" radius={[4, 4, 0, 0]} name="Won revenue" cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div
          data-testid="chart-source"
          className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
                Leads by source · last 6 months
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Cross-check imports across all channels
              </p>
            </div>
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.source_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {sources.map((s) => (
                  <Bar key={s} dataKey={s} stackId="a" fill={SOURCE_COLORS[s]} radius={[2, 2, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          data-testid="chart-status"
          className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
        >
          <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
            Pipeline breakdown
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Current lead statuses</p>
          <div className="h-[240px] mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.status_distribution}
                  dataKey="count"
                  nameKey="status"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {stats.status_distribution.map((entry) => (
                    <Cell
                      key={entry.status}
                      fill={
                        {
                          "New Lead": "#8b5cf6",
                          Contacted: "#2563eb",
                          "In Negotiation": "#d97706",
                          "Deal Won": "#16a34a",
                          "Deal Lost": "#dc2626",
                        }[entry.status]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {stats.status_distribution.map((s) => (
              <div key={s.status} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[s.status].dot}`} />
                  <span className="text-slate-600">{s.status}</span>
                </div>
                <span className="font-semibold text-slate-900">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div
          data-testid="chart-timeline"
          className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
                {selMonth
                  ? `Weekly breakdown · ${
                      (stats.monthly_leads || []).find((m) => m.key === selMonth)?.month || ""
                    }`
                  : "Leads added per month"}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {selMonth
                  ? "Leads added each week — go back to view all months"
                  : "Click any month to drill into its weekly breakdown"}
              </p>
            </div>
            {selMonth ? (
              <button
                data-testid="timeline-back"
                onClick={() => setSelMonth(null)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700"
              >
                ← All months
              </button>
            ) : (
              <TrendingUp size={18} className="text-slate-400" />
            )}
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={selMonth ? stats.weekly_by_month?.[selMonth] || [] : stats.monthly_leads || []}
                onClick={(e) => {
                  if (!selMonth && e && e.activeLabel) {
                    const m = (stats.monthly_leads || []).find((x) => x.month === e.activeLabel);
                    if (m) setSelMonth(m.key);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis
                  dataKey={selMonth ? "week" : "month"}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f1f5f9" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="leads" fill="#2563eb" radius={[4, 4, 0, 0]} name="Leads added" cursor={selMonth ? "default" : "pointer"} />
                <Bar dataKey="won" fill="#16a34a" radius={[4, 4, 0, 0]} name="Deals won" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          data-testid="team-performance"
          className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
        >
          <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
            Sales team performance
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Revenue by rep (won deals)</p>
          <div className="mt-4 space-y-3">
            {stats.team_performance.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">
                No won deals yet. Close your first deal to see revenue here.
              </div>
            )}
            {stats.team_performance.map((rep, idx) => {
              const max = stats.team_performance[0]?.value || 1;
              const pct = Math.max(6, (rep.value / max) * 100);
              return (
                <div key={rep.name} data-testid={`rep-row-${idx}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                        {rep.name.slice(0, 1)}
                      </div>
                      <span className="text-sm font-medium text-slate-800">{rep.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">
                      {formatINR(rep.value)}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                      style={{ width: `${pct}%`, transition: "width 400ms ease" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {team.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500">
              {team.length} active sales rep{team.length !== 1 ? "s" : ""} on roster
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
